<template>
  <v-container fluid>
    <PageHeaderSlot />

    <v-form ref="dataForm" @submit.prevent="">
      <Datatable
        :isLoading="tableLoading"
        :tableHeaders="tableHeaders"
        :tableData="tableData"
        :page.sync="tableFilter.page"
        :pageLimit.sync="tableFilter.limit"
        :itemTotal="tableItemTotal"
        @options-update="getReportData()"
        enableSearchDate
        searchDateYearMonth
        datePlaceholder="table.searchYearMonth"
        enableRefresh
        @refresh-clicked="refreshData()"
        enableExport
        @export-clicked="exportData()"
        :exporting="exportLoading"
        @date-selected="onMonthChange($event)"
        :initSearchDate="tableFilter.month"
        disableSingleDateClear
      >
        <template v-slot:[`item.level_total`]="{ item }">
          <span>HK${{ $formatter.separator($formatter.round(item.level_total)) }}</span>
        </template>
        <template v-slot:[`item.total_salary`]="{ item }">
          <span>HK${{ $formatter.separator($formatter.round(item.total_salary)) }}</span>
        </template>
        <template v-slot:[`item.percentage`]="{ item }">
          <span>HK${{ $formatter.separator($formatter.round(item.percentage)) }}</span>
        </template>
        <template v-slot:[`item.salary_per_hour`]="{ item }">
          <span>HK${{ $formatter.separator($formatter.round(item.salary_per_hour)) }}</span>
        </template>
        <template v-slot:[`item.hours_salary`]="{ item }">
          <span>HK${{ $formatter.separator($formatter.round(item.hours_salary)) }}</span>
        </template>
        <template v-slot:[`item.extra_hour_salary`]="{ item }">
          <span>HK${{ $formatter.separator($formatter.round(item.extra_hour_salary)) }}</span>
        </template>
        <template v-slot:[`item.lesson_bonus`]="{ item }">
          <span>HK${{ $formatter.separator($formatter.round(item.lesson_bonus)) }}</span>
        </template>
        <template v-slot:[`item.bonus`]="{ item }">
          <FormInput
            type="number"
            :fieldValue.sync="item.bonus_value"
            hideDetails
            dispatchUpdateOnChange
            prefix="HK$"
            required
            @input-value="handleBonusChanged(item.staff_id, $event)"
            :readonly="!canEdit"
          />
        </template>
        <template v-slot:[`item.gen_invoice`]="{ item }">
          <FormButton text="downloadPayrollReceipt" small @click="downloadSalaryInvoice(item.staff_id)" />
        </template>
      </Datatable>

      <FormButton v-if="!isAppMode(['T16']) && canEdit" class="mt-4" @click="saveBonus()" />
    </v-form>
  </v-container>
</template>

<script>
import Datatable from '@/components/Datatable.vue'
import ExcelMixin from '@/mixins/ExcelMixin.vue'
import FormInput from '@/components/formField/FormInput.vue'

export default {
  name: 'SalaryReport',
  mixins: [ExcelMixin],
  components: {
    Datatable,
    FormInput,
  },
  computed: {
    tableHeaders() {
      const vm = this
      let headers = [
        { value: 'staff', text: vm.$t('tutor'), width: 180 },
        { value: 'month', text: vm.$t('month'), width: 100 },
        ...(!this.isAppMode(['T16']) ? [{ value: 'bonus', text: vm.$t('bonusThisMonth'), width: 200 }] : []),
        { value: 'total_lesson', text: vm.$t('totalLessonCount'), width: 100 },
        ...(!this.isAppMode(['T16']) ? [{ value: 'level_total', text: vm.$t('levelTotal') }] : []),
        { value: 'hours', text: vm.$t('hourlyWageHour') },
        { value: 'hours_salary', text: vm.$t('totalHourlyWage') },
        ...(this.isAppMode(['T20'])
          ? [
              { value: 'salary_per_hour', text: vm.$t('calendarHourlyWage') },
              { value: 'total_extra_hour', text: vm.$t('calendarTotalHour') },
              { value: 'extra_hour_salary', text: vm.$t('calendarTotalHourlyWage') },
            ]
          : []),
        { value: 'percentage', text: vm.$t('totalShareSalary') },
        ...(this.isAppMode(['T21']) ? [{ value: 'lesson_bonus', text: vm.$t('totalBonus') }] : []),
        { value: 'total_salary', text: vm.$t('totalSalary') },
      ]

      // 薪金簽收單
      if (!this.isAppMode(['T01'])) {
        headers.push({ value: 'gen_invoice', text: '', align: 'end', width: 180 })
      }
      return headers
    },
    canEdit() {
      return !this.isAppMode(['T46']) || this.userType !== 'admin'
    },
  },
  data: vm => ({
    userType: vm.gfGetUserType(),
    tableLoading: false,
    tableData: [],
    tableItemTotal: 0,
    tableFilter: {
      month: '',
      page: 1,
      limit: 10,
    },
    exportLoading: false,
    todayMonth: vm.$formatter.formatDate().substring(0, 7),
  }),
  methods: {
    async getReportData(regenUrl = true) {
      if (regenUrl === true) {
        this._regenerateURL()
      }
      this.tableLoading = true

      try {
        let payload = {
          year_month: this.tableFilter.month || this.todayMonth,
          filter_limit: this.tableFilter.limit,
          filter_page: this.tableFilter.page - 1,
        }
        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) {
          payload['center'] = [selectedSchool]
        }

        const { data, total } = await this.$Fetcher.GetSalaryReports(payload)
        this.tableData = data.map(el => {
          let bonusData = null
          if (this.$validate.DataValid(el.bonus, true)) {
            const bonusList = el.bonus.sort((a, b) => this.$formatter.sortDate(b.create_date, a.create_date))
            bonusData = bonusList[0]
          }

          return {
            ...el,
            staff_id: this.$validate.DataValid(el.staff_id) ? el.staff_id : -1,
            total_salary:
              (el.total_salary || 0) +
              (bonusData ? bonusData.value : 0) +
              (el.salary_per_hour || 0) * (el.total_extra_hour || 0), // 總薪金
            percentage: el.percentage || 0, // 分成總薪金
            hours: el.hours || 0, // 小時（時薪）
            hours_salary: el.hours_salary || 0, // 總時薪
            level_total: this.$salaryLevelOptions.reduce((value, item) => {
              return value + (el[`level_${item.value}_total`] || 0)
            }, 0), // 等級總額
            // -- 本月Bonus
            init_bonus_value: bonusData ? bonusData.value : 0,
            bonus_value: bonusData ? bonusData.value : 0,
            bonus_id: bonusData ? bonusData.id : -1,
            // -- for T20
            total_extra_hour: el.total_extra_hour || 0, //日曆總小時
            extra_hour_salary: (el.salary_per_hour || 0) * (el.total_extra_hour || 0), // 總日曆時薪
            salary_per_hour: el.salary_per_hour || 0, // 日曆時薪
            // -- for T21
            lesson_bonus: el.lesson_bonus || 0, // 總獎金 (R&D + Performance + Attendance)
          }
        })
        this.tableItemTotal = total
      } catch (err) {
        this.$common.error(err)
        this.tableData = []
        if (this.tableFilter.page === 1) {
          this.tableItemTotal = 0
        }
      } finally {
        this.tableLoading = false
      }
    },
    refreshData() {
      this.tableFilter.limit = 10
      this.tableFilter.page = 1
      this.getReportData()
    },
    onMonthChange(value) {
      this.tableFilter.month = value
      this.tableFilter.page = 1
      this.getReportData()
    },

    _getQuery() {
      this.tableFilter.page = 1
      this.tableFilter.limit = 10
      this.tableFilter.month = this.todayMonth
      const query = this.gfGetTableQuery({
        page: 'int',
        limit: 'int',
        month: 'year_month',
      })
      for (const key in this.tableFilter) {
        if (this.$validate.DataValid(query[key])) {
          this.$set(this.tableFilter, key, query[key])
        }
      }
    },
    _regenerateURL() {
      const today = this.todayMonth
      let q = {
        page: this.tableFilter.limit !== 10 || this.tableFilter.page !== 1 ? this.tableFilter.page : null,
        limit: this.tableFilter.limit !== 10 || this.tableFilter.page !== 1 ? this.tableFilter.limit : null,
        month: this.tableFilter.month && this.tableFilter.month !== today ? this.tableFilter.month : null,
      }
      const newQuery = this.$common.diffQuery(q, this.$route.query)
      if (newQuery) this.$router.replace({ query: newQuery })
    },

    // ========= 匯出報表 =========
    async exportData() {
      if (this.exportLoading || this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      this.exportLoading = true
      try {
        let payload = {
          start_date: `${this.tableFilter.month || this.todayMonth}-01`,
          end_date: '',
        }
        const year = parseInt(payload['start_date'].substring(0, 4))
        const month = parseInt(payload['start_date'].substring(5, 7))
        payload['end_date'] = this.$formatter.formatDate(new Date(year, month, 0))

        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) {
          payload['center_id'] = selectedSchool
        }
        await this._downloadReport(payload)
      } catch (err) {
        this.$common.error(err)
      } finally {
        this.exportLoading = false
      }
    },
    _downloadReport(payload = null, count = 0) {
      return new Promise(async (resolve, reject) => {
        try {
          const file = await this.$Fetcher.ExportReport('salary_excel', payload)

          var download = document.createElement('a')
          download.download = file
          download.href = `${this.$mediaPath}${file}`
          download.style.display = 'none'
          download.target = '_blank'
          document.body.appendChild(download)
          download.click()
          download.remove()
          resolve(file)
        } catch (err) {
          this.$common.error(err)
          if (err !== 'abort') {
            if (count < 5) {
              count += 1
              return await this._downloadReport(payload, count)
            } else {
              this.showAlert({
                title: 'message.downloadFail',
                message: err,
                isError: true,
              })
              this.exportLoading = false
            }
            reject(null)
          } else {
            this.exportLoading = false
            reject(null)
          }
        }
      })
    },

    // ========= 本月Bonus =========
    async saveBonus() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }
      if (!this.canEdit) {
        this.$store.dispatch('toggleSnack', { message: 'noPermission' })
        return
      }

      const valid = this.$refs['dataForm'].validate()
      if (!valid) {
        this.$store.dispatch('toggleSnack', { message: 'required' })
        return
      }

      this.$store.dispatch('setLoading', true)

      const newBonusPromises = []
      const updateBonusPromises = []
      const selectedSchool = this.gfGetUserSelectedSchool()
      for (let i = 0; i < this.tableData.length; i++) {
        const item = this.tableData[i]
        let bonus_value = 0
        if (this.$validate.DataValid(item.bonus_value) && parseFloat(item.bonus_value) > 0) {
          bonus_value = parseFloat(item.bonus_value)
        }
        if (item.bonus_id > -1) {
          if (bonus_value !== item.init_bonus_value) {
            let payload = {
              value: bonus_value,
            }
            if (selectedSchool > -1) {
              payload['center_id'] = selectedSchool
            }
            updateBonusPromises.push({
              id: item.bonus_id,
              payload: payload,
            })
          }
        } else if (bonus_value > 0 && item.staff_id > -1) {
          let payload = {
            staff_id: item.staff_id,
            value: bonus_value,
            month: this.tableFilter.month || this.todayMonth,
          }
          if (selectedSchool > -1) {
            payload['center_id'] = selectedSchool
          }
          newBonusPromises.push(payload)
        }
      }

      if (newBonusPromises.length > 0) {
        await this._handleNewBonus(newBonusPromises)
      }
      if (updateBonusPromises.length > 0) {
        await this._handleUpdateBonus(updateBonusPromises)
      }

      this.$store.dispatch('setLoading', false)
      this.$store.dispatch('setDataIsUpdated', false)
      this.$store.dispatch('toggleSnack', {
        message: 'message.saveSuccess',
        type: 'success',
      })
      this.tableFilter.page = 1
      this.getReportData()
    },
    async _handleNewBonus(promises, count = 0, results = []) {
      if (count < promises.length) {
        try {
          await this.$Fetcher.NewStaffBonus(promises[count])
          results.push(true)
        } catch (err) {
          this.$common.error(err)
          results.push(err)
        } finally {
          count += 1
          return await this._handleNewBonus(promises, count, results)
        }
      } else {
        return results
      }
    },
    async _handleUpdateBonus(promises, count = 0, results = []) {
      if (count < promises.length) {
        try {
          await this.$Fetcher.UpdateStaffBonus(promises[count].id, promises[count].payload)
          results.push(true)
        } catch (err) {
          this.$common.error(err)
          results.push(err)
        } finally {
          count += 1
          return await this._handleUpdateBonus(promises, count, results)
        }
      } else {
        return results
      }
    },
    handleBonusChanged(staff_id, value) {
      for (let i = 0; i < this.tableData.length; i++) {
        if (this.tableData[i].staff_id === staff_id) {
          let total_salary =
            this.tableData[i].level_total + this.tableData[i].hours_salary + this.tableData[i].percentage
          if (value && !isNaN(parseFloat(value))) {
            total_salary += parseFloat(value)
          }
          this.$set(this.tableData[i], 'total_salary', total_salary)
          break
        }
      }
    },

    // ========= 下載薪金簽收單 =========
    async downloadSalaryInvoice(staff_id) {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      const downloadWindow = window.open('', '_blank')
      if (!downloadWindow) {
        this.$store.dispatch('toggleSnack', { message: 'message.popupBlocked', type: 'error' })
        return
      }

      this.$store.dispatch('setLoading', true)
      try {
        const selectedSchool = this.gfGetUserSelectedSchool()
        const files = await this.$Fetcher.GenSalaryInvoice(
          (this.tableFilter.month || this.todayMonth).substring(0, 7),
          staff_id,
          selectedSchool,
        )

        if (files.length > 0) {
          this._downloadFile(files[0], downloadWindow)
          for (let i = 1; i < files.length; i++) {
            this._downloadFile(files[i])
          }
        } else {
          downloadWindow.close()
        }
      } catch (err) {
        this.$common.error(err)
        downloadWindow.close()
        this.showAlert({
          title: 'message.downloadFail',
          message: err,
          isError: true,
        })
      }
      this.$store.dispatch('setLoading', false)
    },
    _downloadFile(file, targetWindow = null) {
      if (targetWindow) {
        targetWindow.location.href = `${this.$mediaPath}${file}`
        return
      }

      this.gfOpenWindow(() => {
        return `${this.$mediaPath}${file}`
      })
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: SalaryReport')
      this.userType = this.gfGetUserType()
      this._getQuery()
      this.$store.dispatch('toggleLoadingPage', false)

      await this.getReportData(false)
    },
  },
  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.tableLoading = true
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },

  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    if (this.$store.getters.isLoading) {
      this.$store.dispatch('toggleSnack', { message: 'processing' })
      next(false)
    } else if (this.$store.getters.dataIsUpdated) {
      this.$store.dispatch('showLeaveAlert', { toLink: { name: to.name } })
      next(false)
    } else {
      this.$XHR.abort()
      next()
    }
  },
}
</script>
